<template>
    <page :busy="loading" :tabs="tabs" class="manage-groups-page">
        <grid
            v-if="groups.length"
            ref="grid"
            :columnDefs="columnDefs"
            :rowData="rowData"
            :headerHeight="50"
            :groupDisplayType="'groupRows'"
            :isGroupOpenByDefault="() => true"
            :groupRowRendererParams="groupRowRendererParams"
            :isAutoWidth="isStaff ? false : true"
            :suppressScrollOnNewData="true"
        >
            <responsive-button
                variant="primary"
                :icon="['fas', 'plus']"
                v-if="!isStaff && !false"
                @click="$bvModal.show(modalId)"
            >
                Add Group
            </responsive-button>
            <b-button-group v-if="!isStaff">
                <search-multiselect
                    v-model="selectedColumns"
                    :options="columnsOptions"
                    class="mr-1"
                    variant="primary"
                    placeholder="Columns"
                    controlValue="Columns"
                    itemLabel="column"
                ></search-multiselect>
            </b-button-group>
            <validation-observer v-slot="{ invalid }">
                <modal :id="modalId" title="Create Group" :ok-disabled="invalid" @ok.prevent="createGroup">
                    <validation-provider v-slot="{ errors }" rules="required">
                        <form-input v-model="groupName" floatingLabel="Name" :error="errors[0]" />
                    </validation-provider>
                </modal>
            </validation-observer>
        </grid>
    </page>
</template>

<script>
import API from '../../../api';
import { ADMIN_MANAGE_GROUPS_ENDPOINT } from '../../../endpoints';
import grid from '../../generic/grid/grid.vue';
import page from '../../generic/page/page.vue';
import GroupRowRenderer from '../../owner/edit/owner-edit-access/group-row-renderer.vue';
import AmenitiesCellRenderer from '../../policy/amenity-manager/amenities-cell-renderer.vue';
import ResponsiveButton from '../../generic/responsive-button/responsive-button.vue';
import Modal from '../../generic/modal/modal.vue';
import FormInput from '../../generic/form-input/form-input.vue';
import { formatResponseError } from '../../../utils/api';
import SearchMultiselect from '../../generic/search-multiselect/search-multiselect.vue';

export default {
    components: {
        page,
        grid,
        ResponsiveButton,
        Modal,
        FormInput,
        SearchMultiselect,
        amenitiesCellRenderer: AmenitiesCellRenderer,
        groupRowRenderer: GroupRowRenderer,
    },
    props: {
        isStaff: Boolean,
    },
    data() {
        return {
            groups: [],
            busy: null,
            loading: false,
            rowData: [],
            selectedColumns: [],
            tabs: [
                {
                    title: 'Manage Groups',
                    active: true,
                },
            ],
            groupRowRendererParams: {
                innerRenderer: 'groupRowRenderer',
            },
            modalId: 'create-group-id',
            groupName: null,
            isUpdating: false,
        };
    },
    mounted() {
        this.fetchAllData();
    },

    computed: {
        groupsColumnDefs() {
            return this.groups.map((item, index) => ({
                colId: item.id,
                field: item.name,
                headerName: item.name,
                sortable: false,
                minWidth: 130,
                hide: !this.selectedColumns.includes(item.id),
                headerClass: 'ag-centered-header',
                cellRenderer: 'amenitiesCellRenderer',
                valueGetter: ({ data }) => {
                    return data.allowed_group_ids.includes(item.id);
                },
                cellRendererParams: {
                    onChange: async ({ api, node, data }, value) => {
                        if (!this.isUpdating) {
                            try {
                                this.isUpdating = true;
                                await API.post(
                                    `${ADMIN_MANAGE_GROUPS_ENDPOINT()}set-resource-access/`,

                                    {
                                        resource_id: data.id,
                                        groups: [
                                            {
                                                group_id: item.id,
                                                allow: value,
                                            },
                                        ],
                                    }
                                );
                                this.rowData = this.rowData.map((resource) => {
                                    if (resource.id === data.id) {
                                        let allowedIds = [...resource.allowed_group_ids];
                                        if (value) {
                                            if (!allowedIds.includes(item.id)) {
                                                allowedIds.push(item.id);
                                            }
                                        } else {
                                            allowedIds = allowedIds.filter((id) => item.id !== id);
                                        }
                                        return {
                                            ...resource,
                                            allowed_group_ids: allowedIds,
                                        };
                                    } else {
                                        return resource;
                                    }
                                });
                                this.successToast({
                                    text: `Resource successfully ${value ? 'allowed' : 'denied'}.`,
                                });
                            } catch (e) {
                                const { generic } = formatResponseError(e);

                                this.errorToast({
                                    text: generic || `Could not set ${data.name}`,
                                });
                                api.redrawRows({
                                    rowNodes: [node],
                                });
                                return;
                            } finally {
                                this.isUpdating = false;
                            }
                        }
                    },
                },
            }));
        },
        columnDefs() {
            return [
                {
                    field: 'component',
                    rowGroup: true,
                    hide: true,
                },
                this.selectAllColumn,
                {
                    colId: 'name',
                    field: 'name',
                    headerName: 'Resource Name',
                    headerClass: 'amenity-name-header-cell',
                    sortable: true,
                    minWidth: 220,
                    pinned: 'left',
                    autoHeight: true,
                    cellClass: 'amenity-name-cell',
                },
                ...this.groupsColumnDefs,
            ];
        },
        selectAllColumn() {
            return {
                colId: '',
                field: '',
                headerName: 'Select All',
                cellRenderer: 'amenitiesCellRenderer',
                resizable: false,
                sortable: false,
                valueGetter: ({ data }) => {
                    const containsAll = this.selectedColumns.every((element) => {
                        return data.allowed_group_ids.includes(element);
                    });
                    return containsAll;
                },
                cellRendererParams: {
                    onChange: async ({ data, api, node }, value) => {
                        if (!this.loading) {
                            try {
                                this.loading = true;
                                await API.post(
                                    `${ADMIN_MANAGE_GROUPS_ENDPOINT()}set-resource-access/`,

                                    {
                                        resource_id: data.id,
                                        groups: this.selectedColumns.map((item) => {
                                            return {
                                                group_id: item,
                                                allow: value,
                                            };
                                        }),
                                    }
                                );
                                this.rowData = this.rowData.map((item) => {
                                    if (item.id === data.id) {
                                        const allowedGroups = [
                                            ...new Set([...this.selectedColumns, ...item.allowed_group_ids]),
                                        ];
                                        return {
                                            ...item,
                                            allowed_group_ids: value ? allowedGroups : [],
                                        };
                                    }
                                    return {
                                        ...item,
                                    };
                                });
                            } catch (e) {
                                const { generic } = formatResponseError(e);

                                this.errorToast({
                                    text: generic || `Could not set ${data.name}`,
                                });
                                api.redrawRows({
                                    rowNodes: [node],
                                });
                                return;
                            } finally {
                                this.loading = false;
                            }
                        }
                    },
                },
            };
        },
        columnsOptions() {
            return this.groups.map((item) => ({
                text: item.name,
                value: item.id,
            }));
        },
    },
    methods: {
        async fetchAllData() {
            this.loading = true;
            await this.fetchGroupsList();
            await this.fetchResources();
            this.loading = false;
        },
        async createGroup() {
            try {
                this.loading = true;
                await API.post(`${ADMIN_MANAGE_GROUPS_ENDPOINT()}create-group/`, {
                    id: 0,
                    name: this.groupName,
                });
                this.$bvModal.hide(this.modalId);
                await this.fetchGroupsList();
                await this.fetchResources();
            } catch (e) {
                const { generic } = formatResponseError(e);
                this.errorToast({ text: generic || 'Could not create group' });
            } finally {
                this.loading = false;
            }
        },
        async fetchGroupsList() {
            try {
                const { data } = await API.get(
                    this.isStaff
                        ? `${ADMIN_MANAGE_GROUPS_ENDPOINT()}staff-groups/`
                        : `${ADMIN_MANAGE_GROUPS_ENDPOINT()}groups/`
                );
                this.groups = data.sort((a, b) => a.resources - b.resources);
                this.selectedColumns = this.groups.map((item) => item.id);
            } catch (e) {
                const { generic } = formatResponseError(e);
                this.errorToast({ text: generic || 'Could not fetch groups' });
            }
        },
        async fetchResources() {
            try {
                const { data } = await API.get(
                    `${ADMIN_MANAGE_GROUPS_ENDPOINT()}${this.isStaff ? 'staff-resources' : ''}`,
                    {
                        params: {
                            groups: this.groups.map((item) => item.id).join(','),
                        },
                    }
                );
                this.rowData = data;
            } catch (e) {
                const { generic } = formatResponseError(e);
                this.errorToast({ text: generic || 'Could not fetch groups' });
            }
        },
    },
};
</script>

<style lang="scss" scoped>
@import '~bootstrap/scss/_functions.scss';
@import '~bootstrap/scss/_variables.scss';
@import '~bootstrap/scss/mixins/_breakpoints.scss';
.manage-groups-page {
    :deep(.ag-header-cell) {
        .ag-header-cell-label {
            display: flex;
            justify-content: center;
            width: 100%;
        }
    }

    :deep(.amenity-name-header-cell) {
        border-right: 1px solid #cdcdcd;
    }
    :deep(.amenity-name-cell) {
        border-right: 1px solid #cdcdcd;
        padding-bottom: 5px;
        padding-top: 5px;
        .amenity-name {
            white-space: normal;
            word-break: break-word !important;
        }
        .ag-cell-wrapper {
            .ag-cell-value {
                width: 100% !important;
            }
        }
    }
}
@include media-breakpoint-down(sm) {
    .manage-groups-page {
        .toolbar-input {
            width: 100%;
        }
    }
}
</style>
